import React from 'react';
import { Badge, Button, Checkbox } from 'antd';
import { CalendarOutlined, CommentOutlined, EyeOutlined, ShopOutlined } from '@ant-design/icons';
import styles from './OrderCard.module.css';
import { StatusTag, orderSelectableForBulk } from './OrderDashboard';
import { getCurrencySymbol } from '../../utils/currency';
import { cdnUrl } from '../../config/mediaPaths';

const formatOrderDate = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatOrderAmount = (amount, currency) => {
  const symbol = getCurrencySymbol(currency || 'INR');
  return `${symbol}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const OrderCard = ({
  order,
  selected,
  onSelect,
  onView,
  onNotes,
}) => {
  const imgKey = Array.isArray(order.rawData?.ItemImages) ? order.rawData.ItemImages[0] : '';
  const imgUrl = imgKey && order.rawData?.HubId && order.rawData?.BizId
    ? cdnUrl({ entity: 'item', fileType: 'media', hubId: order.rawData.HubId, bizId: order.rawData.BizId, fileName: imgKey })
    : null;
  const unreadCustNotes = Array.isArray(order.rawData?.OrderNotes)
    ? order.rawData.OrderNotes.filter(
        (note) => (note.by || '').toLowerCase() === 'cust' && note.read !== true
      ).length
    : 0;

  return (
    <div
      className={`${styles.orderCard} ${selected ? styles.orderCardSelected : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onView(order)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onView(order);
        }
      }}
    >
      <div className={styles.orderCardCorner}>
        <span className={styles.orderCardStatus}>
          <StatusTag status={order.status} />
        </span>
      </div>

      <div className={styles.orderCardRow}>
        <Checkbox
          checked={selected}
          disabled={!orderSelectableForBulk(order)}
          onChange={(e) => onSelect(order, e.target.checked)}
          onClick={(event) => event.stopPropagation()}
          aria-label="Select order"
        />

        <div className={styles.orderCardThumb}>
          {imgUrl ? (
            <img src={imgUrl} alt="" className={styles.orderCardThumbImg} />
          ) : (
            <div className={styles.orderCardThumbPlaceholder} aria-hidden="true">
              <ShopOutlined className={styles.orderCardThumbPlaceholderIcon} />
            </div>
          )}
        </div>

        <div className={styles.orderCardInfo}>
          <div className={styles.orderCardOrderId}>{order.orderNumber}</div>
          <div className={styles.orderCardMetaLine}>
            <ShopOutlined className={styles.orderCardMetaIcon} />
            <span>{order.customer}</span>
          </div>
          <div className={styles.orderCardMetaLine}>
            <CalendarOutlined className={styles.orderCardMetaIcon} />
            <span>{formatOrderDate(order.date)}</span>
          </div>
        </div>

        <div className={styles.orderCardAmountSection}>
          <div className={styles.orderCardAmount}>
            {formatOrderAmount(order.amount, order.currency)}
          </div>
          <div className={styles.orderCardAmountLabel}>
            {Number(order.items) || 0} item{Number(order.items) === 1 ? '' : 's'}
          </div>
        </div>

        <div className={styles.orderCardActions}>
          <span className={styles.orderCardNotesWrap}>
            <Badge count={unreadCustNotes} size="small" showZero={false} offset={[6, 0]}>
              <Button
                type="text"
                size="middle"
                icon={<CommentOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  onNotes(order);
                }}
                aria-label="Order Notes"
              />
            </Badge>
          </span>
          <Button
            type="primary"
            size="middle"
            icon={<EyeOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              onView(order);
            }}
          >
            View
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
